import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Trash2, Plus, Pencil, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = { name: '', role: '', location: '', quote: '', rating: 5, order: 0, active: true };

const SiteTestimonialsManager = ({ headers }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(EMPTY);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/site-testimonials/admin`, { headers });
      setItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load testimonials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const openNew = () => { setForm({ ...EMPTY, order: items.length }); setEditing('new'); };
  const openEdit = (t) => { setForm({ name: t.name, role: t.role || '', location: t.location || '', quote: t.quote, rating: t.rating || 5, order: t.order || 0, active: t.active !== false }); setEditing(t.id); };
  const closeForm = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.name.trim() || !form.quote.trim()) { toast.error('Name and quote are required'); return; }
    try {
      if (editing === 'new') {
        await axios.post(`${API_URL}/api/site-testimonials/admin`, form, { headers });
        toast.success('Testimonial added');
      } else {
        await axios.put(`${API_URL}/api/site-testimonials/admin/${editing}`, form, { headers });
        toast.success('Testimonial updated');
      }
      closeForm();
      fetchItems();
    } catch (e) {
      toast.error('Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await axios.delete(`${API_URL}/api/site-testimonials/admin/${id}`, { headers });
      toast.success('Testimonial deleted');
      fetchItems();
    } catch (e) { toast.error('Delete failed'); }
  };

  const toggleActive = async (t) => {
    try {
      await axios.put(`${API_URL}/api/site-testimonials/admin/${t.id}`, {
        name: t.name, role: t.role || '', location: t.location || '', quote: t.quote,
        rating: t.rating || 5, order: t.order || 0, active: !(t.active !== false),
      }, { headers });
      fetchItems();
    } catch (e) { toast.error('Update failed'); }
  };

  return (
    <div className="animate-fade-in" data-testid="site-testimonials-manager">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#003456]">Website Testimonials</h1>
          <p className="text-gray-500 mt-1">These appear below the ROI calculator on the homepage and machine pages.</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-5 py-3 rounded-lg transition-colors" data-testid="testimonial-add-btn">
          <Plus className="h-5 w-5" /> Add testimonial
        </button>
      </div>

      {/* Form */}
      {editing && (
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8 border border-[#CE8946]/30" data-testid="testimonial-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-[#003456]">{editing === 'new' ? 'New testimonial' : 'Edit testimonial'}</h3>
            <button onClick={closeForm} className="text-gray-400 hover:text-gray-700" data-testid="testimonial-form-close"><X className="h-5 w-5" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="testimonial-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role / title</label>
              <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="testimonial-role" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Alberta, CA" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="testimonial-location" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rating</label>
                <select value={form.rating} onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="testimonial-rating">
                  {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n > 1 ? 's' : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="testimonial-order" />
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Quote *</label>
              <textarea value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid="testimonial-quote" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-[#CE8946]" data-testid="testimonial-active" />
              <span className="text-sm font-medium text-gray-700">Show on website</span>
            </label>
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={save} className="bg-[#003456] hover:bg-[#023F63] text-white font-semibold px-6 py-2.5 rounded-lg transition-colors" data-testid="testimonial-save">Save</button>
            <button onClick={closeForm} className="border border-gray-300 text-gray-600 px-6 py-2.5 rounded-lg hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white rounded-2xl shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-gray-400">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <Star className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No testimonials yet</h3>
            <p className="text-gray-500">Click "Add testimonial" to publish your first one.</p>
          </div>
        ) : (
          <div className="divide-y">
            {items.map((t) => (
              <div key={t.id} className="p-6 flex items-start gap-4" data-testid={`testimonial-row-${t.id}`}>
                <span className={`w-11 h-11 rounded-full ${t.color || 'bg-[#003456]'} text-white font-bold flex items-center justify-center flex-shrink-0`}>{t.initials}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-semibold text-[#003456]">{t.name}</span>
                    <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className={`h-4 w-4 ${i < (t.rating || 5) ? 'text-[#CE8946] fill-[#CE8946]' : 'text-gray-300'}`} />)}</div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${t.active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{t.active !== false ? 'Live' : 'Hidden'}</span>
                    <span className="text-xs text-gray-400">Order {t.order ?? 0}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{[t.role, t.location].filter(Boolean).join(' · ')}</p>
                  <p className="text-gray-600 text-sm mt-2">"{t.quote}"</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => toggleActive(t)} className="text-gray-400 hover:text-[#003456] px-2 py-1 text-xs font-medium" data-testid={`testimonial-toggle-${t.id}`}>{t.active !== false ? 'Hide' : 'Show'}</button>
                  <button onClick={() => openEdit(t)} className="text-gray-400 hover:text-[#003456] p-2" data-testid={`testimonial-edit-${t.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(t.id)} className="text-red-400 hover:text-red-600 p-2" data-testid={`testimonial-delete-${t.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteTestimonialsManager;
